import React, { useMemo, memo } from 'react';

const keyframeCSS = `
  @media (max-width: 768px) {
    .prize-board-container {
      max-width: 100% !important;
      padding: 8px 4px !important;
      gap: 4px !important;
    }
    .prize-board-title {
      font-size: 0.6rem !important;
      margin-bottom: 4px !important;
      padding: 4px 0 !important;
    }
    .prize-board-columns { gap: 4px !important; }
    .prize-board-row {
      padding: 3px 6px !important;
      font-size: 0.62rem !important;
      gap: 4px !important;
    }
    .prize-board-dot { width: 5px !important; height: 5px !important; }
    .prize-board-label { font-size: 0.5rem !important; margin-bottom: 2px !important; }
  }
  @keyframes prizePulse {
    0%, 100% { box-shadow: inset 0 0 8px rgba(255,255,255,0.04), 0 0 4px rgba(0,0,0,0.2); }
    50% { box-shadow: inset 0 0 14px rgba(255,255,255,0.1), 0 0 8px rgba(0,0,0,0.1); }
  }
  @keyframes dotGlow {
    0%, 100% { box-shadow: 0 0 4px currentColor, 0 0 8px currentColor; }
    50% { box-shadow: 0 0 8px currentColor, 0 0 16px currentColor, 0 0 24px currentColor; }
  }
  @keyframes neonTitle {
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
      text-shadow: 0 0 10px rgba(212,175,55,0.7), 0 0 20px rgba(212,175,55,0.4), 0 0 40px rgba(212,175,55,0.15);
    }
    20%, 24%, 55% { text-shadow: 0 0 5px rgba(212,175,55,0.3); }
  }
  @keyframes eliminateFlash {
    0% { opacity: 1; transform: scale(1); }
    30% { opacity: 0.1; transform: scale(0.98); }
    60% { opacity: 0.5; transform: scale(1); }
    100% { opacity: 0.25; transform: scale(1); }
  }
  @keyframes boardGlow {
    0%, 100% { box-shadow: inset 0 0 30px rgba(0,0,0,0.5), 0 0 10px rgba(0,0,0,0.3); }
    50% { box-shadow: inset 0 0 30px rgba(0,0,0,0.4), 0 0 15px rgba(212,175,55,0.05); }
  }
  @keyframes ledBlink {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 1; }
  }
`;

function formatPrize(prize) {
  if (prize && typeof prize === 'object' && 'label' in prize) {
    if (prize.value === 0 && prize.label) return prize.label;
    return formatEuro(prize.value);
  }
  return formatEuro(prize);
}

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  if (value >= 1000) {
    return '\u20AC' + value.toLocaleString('it-IT');
  }
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export default function PrizeBoard({ boxes = [], allPrizes = [] }) {
  const prizeList = useMemo(() => {
    const normalized = allPrizes.map((p) =>
      typeof p === 'object' && p !== null ? { value: p.value ?? 0, label: p.label ?? null } : { value: p, label: null }
    );
    const sorted = [...normalized].sort((a, b) => a.value - b.value);

    const getKey = (p) => (p.value === 0 && p.label ? `text:${p.label}` : `val:${p.value}`);

    const eliminatedCounts = new Map();
    boxes.forEach((box) => {
      if (box.is_opened && (box.prize_value !== undefined || box.prize_label)) {
        const key = (box.prize_value === 0 || box.prize_value === undefined) && box.prize_label
          ? `text:${box.prize_label}` : `val:${box.prize_value}`;
        eliminatedCounts.set(key, (eliminatedCounts.get(key) || 0) + 1);
      }
    });

    const usedCounts = new Map();
    return sorted.map((prize) => {
      const key = getKey(prize);
      const elimCount = eliminatedCounts.get(key) || 0;
      const usedSoFar = usedCounts.get(key) || 0;
      const isEliminated = usedSoFar < elimCount;
      usedCounts.set(key, usedSoFar + 1);
      return { ...prize, eliminated: isEliminated };
    });
  }, [allPrizes, boxes]);

  const mid = Math.ceil(prizeList.length / 2);
  const lowPrizes = prizeList.slice(0, mid);
  const highPrizes = prizeList.slice(mid);

  return (
    <div className="prize-board-container" style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      padding: '16px 12px',
      maxWidth: '300px',
      width: '100%',
      background: 'linear-gradient(180deg, rgba(5,5,18,0.95) 0%, rgba(8,8,25,0.98) 100%)',
      borderRadius: '12px',
      border: '1px solid rgba(212,175,55,0.1)',
      animation: 'boardGlow 4s ease-in-out infinite',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{keyframeCSS}</style>

      {/* Top LED strip */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)',
        animation: 'ledBlink 3s ease-in-out infinite',
      }} />

      {/* Corner accent lights */}
      <div style={{
        position: 'absolute',
        top: '4px',
        left: '4px',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: 'rgba(212,175,55,0.5)',
        boxShadow: '0 0 6px rgba(212,175,55,0.4)',
        animation: 'ledBlink 2s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: 'rgba(212,175,55,0.5)',
        boxShadow: '0 0 6px rgba(212,175,55,0.4)',
        animation: 'ledBlink 2s ease-in-out infinite',
        animationDelay: '1s',
      }} />

      <div className="prize-board-title" style={{
        fontFamily: 'var(--font-display)',
        fontSize: '0.85rem',
        fontWeight: 700,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: '#D4AF37',
        textAlign: 'center',
        marginBottom: '10px',
        animation: 'neonTitle 3s ease-in-out infinite',
        padding: '8px 0',
        borderTop: '1px solid rgba(212,175,55,0.15)',
        borderBottom: '1px solid rgba(212,175,55,0.15)',
        position: 'relative',
      }}>
        PREMI IN GIOCO
      </div>

      <div className="prize-board-columns" style={{
        display: 'flex',
        gap: '8px',
        width: '100%',
      }}>
        {/* Low prizes - Blue */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
          <div className="prize-board-label" style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#3498db',
            textAlign: 'center',
            marginBottom: '6px',
            textShadow: '0 0 12px rgba(52,152,219,0.5)',
          }}>Bassi</div>
          {lowPrizes.map((prize, i) => (
            <PrizeRow key={`low-${i}`} prize={prize} isLow={true} index={i} />
          ))}
        </div>

        {/* Divider */}
        <div style={{
          width: '1px',
          background: 'linear-gradient(180deg, transparent, rgba(212,175,55,0.2), rgba(212,175,55,0.15), transparent)',
          alignSelf: 'stretch',
        }} />

        {/* High prizes - Red */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
          <div className="prize-board-label" style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#e74c3c',
            textAlign: 'center',
            marginBottom: '6px',
            textShadow: '0 0 12px rgba(231,76,60,0.5)',
          }}>Alti</div>
          {highPrizes.map((prize, i) => (
            <PrizeRow key={`high-${i}`} prize={prize} isLow={false} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

const PrizeRow = memo(function PrizeRow({ prize, isLow, index }) {
  const eliminated = prize.eliminated;

  const activeColor = isLow ? '#3498db' : '#e74c3c';
  const activeBg = isLow
    ? 'linear-gradient(135deg, rgba(20,30,100,0.8), rgba(30,45,120,0.6))'
    : 'linear-gradient(135deg, rgba(160,20,20,0.8), rgba(180,30,30,0.6))';

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '5px 10px',
    borderRadius: '4px',
    background: eliminated ? 'rgba(30,30,40,0.3)' : activeBg,
    border: eliminated
      ? '1px solid rgba(50,50,60,0.2)'
      : `1px solid ${activeColor}40`,
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: eliminated ? 'rgba(80,80,100,0.35)' : '#f0f0f5',
    textDecoration: eliminated ? 'line-through' : 'none',
    opacity: eliminated ? 0.3 : 1,
    animation: eliminated ? 'none' : 'prizePulse 3s ease-in-out infinite',
    animationDelay: `${index * 0.15}s`,
    transition: 'all 0.4s ease',
    position: 'relative',
    overflow: 'hidden',
    justifyContent: isLow ? 'flex-start' : 'flex-end',
    flexDirection: isLow ? 'row' : 'row-reverse',
  };

  // LED dot indicator
  const dotStyle = {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: eliminated ? 'rgba(60,60,70,0.3)' : activeColor,
    flexShrink: 0,
    animation: eliminated ? 'none' : 'dotGlow 2s ease-in-out infinite',
    animationDelay: `${index * 0.1}s`,
    color: activeColor,
    transition: 'all 0.4s ease',
  };

  return (
    <div className="prize-board-row" style={rowStyle}>
      <div className="prize-board-dot" style={dotStyle} />
      <span style={{
        flex: 1,
        textAlign: isLow ? 'left' : 'right',
        whiteSpace: 'nowrap',
        textShadow: eliminated ? 'none' : `0 0 8px ${activeColor}30`,
      }}>
        {formatPrize(prize)}
      </span>
    </div>
  );
});
